import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api/client";


const BLOCKABLE_TYPES = new Set([
  "brute_force_login",
  "suspicious_remote_connection",
]);


function BlockedIpsPanel({
  companyId,
  role,
  allowedDeviceUuids = [],
}) {
  const effectiveCompanyId =
    String(companyId || "");

  const withCompany = (path) => {
    if (!effectiveCompanyId) return path;

    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}company_id=${encodeURIComponent(effectiveCompanyId)}`;
  };
  const [requests, setRequests] = useState([]);
  const [detections, setDetections] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const allowedDeviceSet = useMemo(
    () =>
      new Set(
        (Array.isArray(allowedDeviceUuids)
          ? allowedDeviceUuids
          : []
        )
          .map((value) => String(value || ""))
          .filter(Boolean)
      ),
    [allowedDeviceUuids]
  );

  const load = async () => {
    try {
      setError("");

      if (!effectiveCompanyId) {
        setRequests([]);
        setDetections([]);
        setSummary({});
        setLoading(false);
        return;
      }

      const [
        requestResponse,
        summaryResponse,
        detectionResponse,
      ] = await Promise.all([
        apiFetch(withCompany("/network-protection/block-requests?limit=100")),
        apiFetch(withCompany("/network-protection/block-requests/summary")),
        apiFetch(withCompany("/network-protection/detections?limit=100")),
      ]);

      if (!requestResponse.ok) {
        throw new Error("Unable to load Network Protection response requests");
      }

      if (!summaryResponse.ok) {
        throw new Error("Unable to load Network Protection response summary");
      }

      if (!detectionResponse.ok) {
        throw new Error("Unable to load canonical Network Protection detections");
      }

      const requestData = await requestResponse.json();
      const summaryData = await summaryResponse.json();
      const detectionData = await detectionResponse.json();

      setRequests(
        Array.isArray(requestData)
          ? requestData
          : Array.isArray(requestData?.requests)
            ? requestData.requests
            : []
      );

      setSummary(
        summaryData && typeof summaryData === "object"
          ? summaryData
          : {}
      );

      setDetections(
        Array.isArray(detectionData)
          ? detectionData
          : Array.isArray(detectionData?.detections)
            ? detectionData.detections
            : []
      );
    } catch (err) {
      setError(
        err?.message ||
          "Unable to load Network Protection response controls"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    const timer = setInterval(
      load,
      30000
    );

    return () => clearInterval(timer);

    // load is intentionally rebound to the active tenant scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCompanyId]);

  const activeTargets = useMemo(
    () =>
      new Set(
        requests
          .filter((item) =>
            [
              "pending_approval",
              "approved",
              "applying",
              "applied",
              "release_pending",
            ].includes(item.status)
          )
          .map(
            (item) =>
              `${item.device_uuid || ""}|${item.target_network || ""}`
          )
      ),
    [requests]
  );

  const scopedDetections = useMemo(
    () =>
      detections.filter(
        (item) =>
          allowedDeviceSet.has(
            String(item.device_uuid || "")
          )
      ),
    [detections, allowedDeviceSet]
  );

  const scopedRequests = useMemo(
    () =>
      requests.filter(
        (item) =>
          allowedDeviceSet.has(
            String(item.device_uuid || "")
          )
      ),
    [requests, allowedDeviceSet]
  );

  const candidates = useMemo(
    () =>
      scopedDetections
        .filter((item) =>
          BLOCKABLE_TYPES.has(item.detection_type)
        )
        .filter((item) =>
          !["resolved", "closed"].includes(
            String(item.status || "").toLowerCase()
          )
        )
        .slice(0, 20),
    [scopedDetections]
  );

  const callAction = async (
    key,
    path,
    options
  ) => {
    try {
      setBusy(key);
      setError("");

      const response = await apiFetch(
        path,
        options
      );

      if (!response.ok) {
        let detail = "";

        try {
          const body = await response.json();
          detail = body?.detail
            ? `: ${body.detail}`
            : "";
        } catch {
          detail = "";
        }

        throw new Error(
          `Network Protection action failed${detail}`
        );
      }

      await load();
    } catch (err) {
      setError(
        err?.message ||
          "Network Protection action failed"
      );
    } finally {
      setBusy("");
    }
  };

  const requestBlock = async (detection) => {
    const rawDuration = window.prompt(
      "Block duration in minutes (5-1440)",
      "60"
    );

    if (rawDuration === null) {
      return;
    }

    const duration = Number(
      rawDuration
    );

    if (
      !Number.isInteger(duration) ||
      duration < 5 ||
      duration > 1440
    ) {
      setError(
        "Block duration must be an integer from 5 through 1440 minutes."
      );
      return;
    }

    const reason = window.prompt(
      "Reason for this block request",
      `Response to ${detection.detection_type || "network detection"}`
    );

    if (reason === null) {
      return;
    }

    await callAction(
      `request:${detection.detection_uuid}`,
      withCompany("/network-protection/block-requests"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          detection_uuid: detection.detection_uuid,
          duration_minutes: duration,
          reason,
        }),
      }
    );
  };

  const approve = async (item) => {
    if (
      !window.confirm(
        `Approve the temporary block for ${item.target_network}?`
      )
    ) {
      return;
    }

    await callAction(
      `approve:${item.request_uuid}`,
      `/network-protection/block-requests/${item.request_uuid}/approve`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reason: "Approved from Network Protection Center",
        }),
      }
    );
  };

  const cancel = async (item) => {
    if (
      !window.confirm(
        `Cancel the pending block request for ${item.target_network}?`
      )
    ) {
      return;
    }

    await callAction(
      `cancel:${item.request_uuid}`,
      `/network-protection/block-requests/${item.request_uuid}/cancel`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reason: "Cancelled from Network Protection Center",
        }),
      }
    );
  };

  const release = async (item) => {
    if (
      !window.confirm(
        `Release the active temporary block for ${item.target_network}?`
      )
    ) {
      return;
    }

    await callAction(
      `release:${item.request_uuid}`,
      `/network-protection/block-requests/${item.request_uuid}/release`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reason: "Released from Network Protection Center",
        }),
      }
    );
  };

  if (loading) {
    return (
      <section style={styles.panel}>
        <h2 style={styles.title}>Network Response Controls</h2>
        <p style={styles.muted}>
          Loading canonical detections and response state...
        </p>
      </section>
    );
  }

  return (
    <section style={styles.panel}>
      <div style={styles.header}>
        <div>
          <div style={styles.pill}>
            Congo Market AI • Approved Response
          </div>

          <h2 style={styles.title}>
            Network Response Controls
          </h2>

          <p style={styles.subtitle}>
            Correlates canonical Network Protection detections with
            administrator-approved, temporary endpoint firewall responses.
            Detection never causes an automatic block.
          </p>
        </div>

        <button
          style={styles.refreshButton}
          onClick={load}
          disabled={Boolean(busy)}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div style={styles.errorBox}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={styles.summaryRow}>
        <SummaryCard
          label="Pending Approval"
          value={summary?.pending_approval || 0}
        />

        <SummaryCard
          label="Applied"
          value={summary?.applied || 0}
        />

        <SummaryCard
          label="Failed"
          value={summary?.failed || 0}
        />

        <SummaryCard
          label="Released"
          value={summary?.released || 0}
        />

        <SummaryCard
          label="Overdue Reports"
          value={summary?.overdue_expiration_reports || 0}
        />
      </div>

      <div style={styles.sectionTitle}>
        Blockable Canonical Detections
      </div>

      {candidates.length === 0 ? (
        <div style={styles.emptyState}>
          No open brute-force or suspicious-remote-connection detections
          currently qualify for a temporary response request.
        </div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Severity</th>
                <th style={styles.th}>Detection</th>
                <th style={styles.th}>Host</th>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Destination</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {candidates.map((item) => (
                <tr key={item.detection_uuid || item.id}>
                  <td style={styles.td}>
                    <StatusBadge
                      value={item.severity || "unknown"}
                    />
                  </td>

                  <td style={styles.td}>
                    <strong>
                      {item.title || item.detection_type || "Detection"}
                    </strong>
                    <div style={styles.small}>
                      {item.detection_type || "N/A"}
                    </div>
                  </td>

                  <td style={styles.td}>
                    {item.hostname || item.device_uuid || "N/A"}
                  </td>

                  <td style={styles.td}>
                    {item.source_address || "N/A"}
                  </td>

                  <td style={styles.td}>
                    {item.destination_address || "N/A"}
                  </td>

                  <td style={styles.td}>
                    <button
                      style={styles.actionButton}
                      disabled={
                        busy === `request:${item.detection_uuid}` ||
                        !item.detection_uuid
                      }
                      onClick={() => requestBlock(item)}
                    >
                      Request Block
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={styles.sectionTitle}>
        Response Request History
      </div>

      {scopedRequests.length === 0 ? (
        <div style={styles.emptyState}>
          No canonical Network Protection block requests exist yet.
        </div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Target</th>
                <th style={styles.th}>Detection</th>
                <th style={styles.th}>Device</th>
                <th style={styles.th}>Expires</th>
                <th style={styles.th}>Transport</th>
                <th style={styles.th}>Control</th>
              </tr>
            </thead>

            <tbody>
              {scopedRequests.map((item) => (
                <tr key={item.request_uuid || item.id}>
                  <td style={styles.td}>
                    <StatusBadge
                      value={item.status || "unknown"}
                    />
                  </td>

                  <td style={styles.td}>
                    <strong>
                      {item.target_network || "N/A"}
                    </strong>
                  </td>

                  <td style={styles.td}>
                    {item.detection_title ||
                      item.detection_type ||
                      item.detection_uuid ||
                      "N/A"}
                  </td>

                  <td style={styles.td}>
                    {item.hostname ||
                      item.device_uuid ||
                      "N/A"}
                  </td>

                  <td style={styles.td}>
                    {formatDate(item.expires_at)}
                  </td>

                  <td style={styles.td}>
                    {item.transport_status ||
                      item.release_transport_status ||
                      "N/A"}
                  </td>

                  <td style={styles.td}>
                    <div style={styles.actions}>
                      {item.status === "pending_approval" && (
                        <>
                          <button
                            style={styles.actionButton}
                            disabled={
                              busy === `approve:${item.request_uuid}`
                            }
                            onClick={() => approve(item)}
                          >
                            Approve
                          </button>

                          <button
                            style={styles.secondaryButton}
                            disabled={
                              busy === `cancel:${item.request_uuid}`
                            }
                            onClick={() => cancel(item)}
                          >
                            Cancel
                          </button>
                        </>
                      )}

                      {item.status === "applied" && (
                        <button
                          style={styles.secondaryButton}
                          disabled={
                            busy === `release:${item.request_uuid}`
                          }
                          onClick={() => release(item)}
                        >
                          Release
                        </button>
                      )}

                      {![
                        "pending_approval",
                        "applied",
                      ].includes(item.status) && (
                        <span style={styles.small}>
                          No manual action
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={styles.footerNote}>
        Firewall namespace: <strong>WirelessPress Network Response</strong>.
        Permanent WirelessPress Policy firewall rules are managed separately.
      </div>
    </section>
  );
}


function SummaryCard({ label, value }) {
  return (
    <div style={styles.summaryCard}>
      <span style={styles.summaryLabel}>
        {label}
      </span>

      <strong style={styles.summaryValue}>
        {value}
      </strong>
    </div>
  );
}


function StatusBadge({ value }) {
  const normalized = String(
    value || "unknown"
  ).toLowerCase();

  return (
    <span
      style={{
        ...styles.badge,
        ...(normalized.includes("fail") ||
        normalized === "high" ||
        normalized === "critical"
          ? styles.badgeRed
          : normalized.includes("applied") ||
              normalized.includes("released") ||
              normalized === "low"
            ? styles.badgeGreen
            : styles.badgeAmber),
      }}
    >
      {String(value || "unknown").replaceAll("_", " ")}
    </span>
  );
}


function formatDate(value) {
  if (!value) {
    return "N/A";
  }

  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}


const styles = {
  panel: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    padding: "22px",
    marginBottom: "18px",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.08)",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: "18px",
    alignItems: "flex-start",
    marginBottom: "18px",
  },

  pill: {
    display: "inline-flex",
    padding: "7px 11px",
    borderRadius: "999px",
    background: "#dbeafe",
    color: "#1e3a8a",
    fontSize: "12px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "10px",
  },

  title: {
    margin: 0,
    fontSize: "26px",
    letterSpacing: "-0.04em",
    color: "#0f172a",
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    lineHeight: 1.6,
    maxWidth: "900px",
  },

  muted: {
    color: "#64748b",
  },

  refreshButton: {
    border: 0,
    background: "#0f172a",
    color: "#ffffff",
    padding: "12px 16px",
    borderRadius: "14px",
    fontWeight: "900",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: "14px",
    padding: "12px 14px",
    marginBottom: "16px",
  },

  summaryRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "12px",
    marginBottom: "22px",
  },

  summaryCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    padding: "16px",
  },

  summaryLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "900",
    textTransform: "uppercase",
  },

  summaryValue: {
    display: "block",
    marginTop: "8px",
    fontSize: "30px",
    color: "#0f172a",
  },

  sectionTitle: {
    fontSize: "16px",
    fontWeight: "900",
    color: "#0f172a",
    margin: "22px 0 10px",
  },

  emptyState: {
    padding: "18px",
    borderRadius: "18px",
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    lineHeight: 1.6,
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1050px",
  },

  th: {
    textAlign: "left",
    background: "#f1f5f9",
    color: "#334155",
    padding: "12px",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderBottom: "1px solid #e2e8f0",
  },

  td: {
    padding: "12px",
    color: "#334155",
    fontSize: "13px",
    borderBottom: "1px solid #e2e8f0",
    verticalAlign: "top",
  },

  badge: {
    display: "inline-flex",
    padding: "5px 9px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "900",
    textTransform: "uppercase",
  },

  badgeRed: {
    background: "#fee2e2",
    color: "#991b1b",
  },

  badgeGreen: {
    background: "#dcfce7",
    color: "#166534",
  },

  badgeAmber: {
    background: "#fef3c7",
    color: "#92400e",
  },

  actions: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  actionButton: {
    border: 0,
    background: "#1d4ed8",
    color: "#ffffff",
    borderRadius: "10px",
    padding: "8px 11px",
    cursor: "pointer",
    fontWeight: "800",
  },

  secondaryButton: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    borderRadius: "10px",
    padding: "8px 11px",
    cursor: "pointer",
    fontWeight: "800",
  },

  small: {
    color: "#64748b",
    fontSize: "11px",
    marginTop: "4px",
  },

  footerNote: {
    marginTop: "18px",
    padding: "12px 14px",
    borderRadius: "14px",
    background: "#eff6ff",
    color: "#1e3a8a",
    fontSize: "12px",
  },
};


export default BlockedIpsPanel;
